import type { ReactNode } from 'react';
import { CounterAdjustment, type CounterAdjustmentSection } from '@tempmee/react-blocks';
import { SolidCircleDollarIcon, SolidCircleHalfIcon } from '@tempmee/react-icons';
import { isPartialShift } from '~/commonComponents/CommonFunctions';
import { IJob, IMultipleCounters } from '~/models';
import { formatMoney } from '~/utils/formatMoney';
import { hasCounterRate } from '../../shared/utils/candidateCardProps';
import { formatCounterDuration, formatCounterRange } from '../utils/counterTerms';

const ICON_PX = 12;

/**
 * A time range with its duration, e.g. "8:30 AM - 4:00 PM (7.5 hours)".
 *
 * The parenthetical is italic on its own, as the design draws it, so this is a
 * node rather than one string.
 */
const timeRange = (start?: string, end?: string): ReactNode => {
  const range = formatCounterRange(start, end);
  const duration = formatCounterDuration(start, end);

  return duration ? (
    <>
      {range} <em>({duration})</em>
    </>
  ) : (
    range
  );
};

export interface ICounterAdjustmentPanelProps {
  job: Partial<IJob> | null | undefined;
  counter: IMultipleCounters | null | undefined;
}

/**
 * The counter offer the selected candidate is proposing: the shift's hours and
 * rate as posted, struck through, above what the candidate is asking for.
 *
 * All this owns is the mapping — which sections apply and how each value reads.
 * The `CounterAdjustment` block draws them, and answers to its own width for the
 * side-by-side/stacked split, so there is no layout prop to thread through.
 *
 * Renders nothing when the candidate accepted the shift as posted, and drops the
 * section that did not change when only one term was countered.
 */
export const CounterAdjustmentPanel = ({ job, counter }: ICounterAdjustmentPanelProps) => {
  const hasPartialShift = Boolean(isPartialShift(counter));
  const hasRateChange = hasCounterRate(counter, job);

  if (!hasPartialShift && !hasRateChange) {
    return null;
  }

  const sections: CounterAdjustmentSection[] = [];

  if (hasPartialShift) {
    sections.push({
      heading: 'Partial Shift',
      icon: <SolidCircleHalfIcon size={ICON_PX} />,
      testID: 'counter-adjustment-hours',
      rows: [
        {
          label: 'Hours',
          value: timeRange(job?.local_start_time, job?.local_end_time),
          variant: 'original',
        },
        { label: 'Counter', value: timeRange(counter?.local_begin, counter?.local_end) },
      ],
    });
  }

  if (hasRateChange) {
    sections.push({
      heading: 'Counter Rate',
      icon: <SolidCircleDollarIcon size={ICON_PX} />,
      testID: 'counter-adjustment-rate',
      rows: [
        { label: 'Rate', value: `${formatMoney(job?.rate)}/hr`, variant: 'original' },
        { label: 'Counter', value: formatMoney(counter?.candidateRate) },
      ],
    });
  }

  return (
    <CounterAdjustment
      sections={sections}
      testID="counter-adjustment-panel"
      data-cy="counter-adjustment-panel"
    />
  );
};
